import type { TaskRelatedKind } from '@palaccount/contracts';
import { Plus } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router';

import { Button } from '@/components/ui/button';
import { Ltr } from '@/components/amount';
import { Pager } from '@/components/pager';
import { usePagedState, usePageRange } from '@/lib/paging';
import { cn } from '@/lib/utils';

import { useTasks } from './api';
import { dueText } from './task-format';

/** An embedded panel, not a screen: ten rows before it pushes its host. */
const PANEL_PAGE_SIZE = 10;

/**
 * The embedded follow-up task panel (CRM-03 §11 "Feeds") — the same engine's
 * rows, narrowed to the record being viewed. Its Add task button opens the
 * Follow-ups screen with the related kind, key and name already filled
 * (BR-CRM-03-15).
 */
export function TaskPanel({
  relatedKind,
  relatedId,
  relatedKey,
  relatedName,
}: {
  relatedKind: TaskRelatedKind;
  relatedId: string;
  relatedKey: string;
  relatedName: string;
}) {
  const { t } = useTranslation();
  /*
   * NFR-42 — bounded by the RECORD (`relatedId`) and then by a page. Ten rows
   * is what an embedded panel shows before it starts pushing the screen it is
   * embedded in; the pager appears only when there is a second page, so a
   * record with three tasks looks exactly as it did.
   */
  const paging = usePagedState(PANEL_PAGE_SIZE, 'tasks');
  const { data, isPending } = useTasks({ scope: 'all', relatedId }, paging.page, PANEL_PAGE_SIZE);
  const tasks = data?.tasks ?? [];
  const range = usePageRange(data, PANEL_PAGE_SIZE);

  const addTaskHref = `/partners/followups?new=1&relatedKind=${relatedKind}&relatedId=${relatedId}&relatedKey=${encodeURIComponent(relatedKey)}&relatedName=${encodeURIComponent(relatedName)}`;

  return (
    <div className="grid gap-2 rounded-md border p-3">
      <div className="flex items-center gap-2">
        <h4 className="me-auto text-sm font-bold">{t('crm.taskPanelTitle')}</h4>
        <Button asChild size="sm" variant="outline">
          <Link to={addTaskHref}>
            <Plus aria-hidden /> {t('crm.addTask')}
          </Link>
        </Button>
      </div>
      {isPending ? (
        /*
         * NFR-43 — while the read is in flight the panel shows that it is
         * working, in the shape of the rows it is fetching. Before this
         * branch it showed the EMPTY state below — "no tasks point at this
         * record yet", a false sentence about data nobody had read yet.
         * `nfr43-loading-states.test.tsx` parks the query in flight and
         * holds both halves.
         */
        <div className="grid gap-2" aria-busy>
          <div className="h-4 animate-pulse rounded bg-muted" />
          <div className="h-4 animate-pulse rounded bg-muted" />
        </div>
      ) : tasks.length === 0 ? (
        <p className="text-xs text-muted-foreground">{t('crm.taskPanelEmpty')}</p>
      ) : (
        tasks.map((task) => (
          <div key={task.id} className="flex items-center gap-2 text-sm">
            <span
              className={cn(
                'me-auto',
                (task.status === 'completed' || task.status === 'cancelled') && 'line-through text-muted-foreground',
              )}
            >
              {task.title}
            </span>
            <span className={cn('text-xs', task.overdue ? 'font-bold text-red-600 dark:text-red-400' : 'text-muted-foreground')}>
              <Ltr value={dueText(t, task.dueAt)} className="font-mono" />
            </span>
          </div>
        ))
      )}
      {range.pages > 1 ? (
        <Pager
          page={paging.page}
          pages={range.pages}
          first={range.first}
          last={range.last}
          total={range.total}
          onPage={paging.setPage}
          label={t('crm.taskPanelTitle')}
        />
      ) : null}
    </div>
  );
}
